import React, { useEffect, useState } from 'react';

const ForecastDateSelector = ({ selectedDate, onDateChange, availableDaysCount = 7, severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0, NO_DATA: 0 } }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 768 && window.innerWidth > 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      setIsTablet(window.innerWidth <= 768 && window.innerWidth > 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDateDisplay = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Generate forecast dates starting from today (only available days, max 6)
  const generateForecastDates = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Use availableDaysCount but cap at 6 days (not 7)
    const daysToShow = Math.min(availableDaysCount, 6);
    
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const forecastDates = generateForecastDates();

  // Find current selected index
  const selectedIndex = forecastDates.findIndex(date => {
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return date.getTime() === selected.getTime();
  });

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      onDateChange(forecastDates[selectedIndex - 1]);
    }
  };

  const handleNext = () => {
    if (selectedIndex < forecastDates.length - 1) {
      onDateChange(forecastDates[selectedIndex + 1]);
    }
  };

  const getLabelForDate = (date, index) => {
    if (index === 0) return 'Today';
    if (index === 1) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: isTablet ? '0.4rem' : '0.5rem',
    padding: isMobile ? '0.5rem' : (isTablet ? '0.4rem 0.6rem' : '0.5rem 0.75rem'),
    background: '#f8f9fa',
    borderTop: '1px solid #dee2e6',
    marginTop: 'auto',
    flexWrap: 'nowrap',
    minWidth: '420px'
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: isMobile ? '0.4rem 0.5rem' : (isTablet ? '0.3rem 0.5rem' : '0.3rem 0.6rem'),
    fontSize: isMobile ? '0.85rem' : (isTablet ? '0.9rem' : '0.95rem'),
    minWidth: isMobile ? '36px' : (isTablet ? '32px' : '32px'),
    minHeight: isMobile ? '36px' : 'auto',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  const labelStyle = {
    fontSize: isMobile ? '0.7rem' : (isTablet ? '0.72rem' : '0.75rem'),
    fontWeight: 600,
    color: '#495057',
    whiteSpace: isMobile ? 'normal' : 'nowrap',
    minWidth: isMobile ? 'auto' : 'fit-content'
  };

  const inputStyle = {
    padding: isMobile ? '0.4rem 0.5rem' : (isTablet ? '0.3rem 0.45rem' : '0.35rem 0.5rem'),
    fontSize: isMobile ? '0.85rem' : (isTablet ? '0.9rem' : '0.95rem'),
    border: '1px solid #ddd',
    borderRadius: '4px',
    background: '#fff',
    minWidth: isMobile ? '120px' : (isTablet ? '130px' : '140px'),
    fontFamily: 'inherit',
    cursor: 'pointer'
  };

  return (
    <div style={containerStyle}>
      {/* Left Arrow */}
      <button
        onClick={handlePrevious}
        disabled={selectedIndex <= 0}
        style={{
          ...buttonStyle,
          opacity: selectedIndex <= 0 ? 0.5 : 1,
          cursor: selectedIndex <= 0 ? 'not-allowed' : 'pointer'
        }}
        title="Previous Day"
      >
        ←
      </button>

      {/* Date Display */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={labelStyle}>Forecast Date:</span>
        <input
          type="text"
          value={`${getLabelForDate(new Date(selectedDate), selectedIndex)}, ${formatDateDisplay(new Date(selectedDate))}`}
          readOnly
          style={inputStyle}
          title="Selected forecast date"
        />
      </div>

      {/* Right Arrow */}
      <button
        onClick={handleNext}
        disabled={selectedIndex >= forecastDates.length - 1}
        style={{
          ...buttonStyle,
          opacity: selectedIndex >= forecastDates.length - 1 ? 0.5 : 1,
          cursor: selectedIndex >= forecastDates.length - 1 ? 'not-allowed' : 'pointer'
        }}
        title="Next Day"
      >
        →
      </button>

      {/* Severity Legend - next to right arrow, aligned with date selector row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginLeft: '0.75rem',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
          <div style={{ width: '10px', height: '10px', background: '#2E7D32', borderRadius: '2px', flexShrink: 0 }} />
          <span style={{ fontSize: '0.7rem', color: '#495057', fontWeight: 500 }}>Low ({severityCounts.LOW || 0})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
          <div style={{ width: '10px', height: '10px', background: '#F9A825', borderRadius: '2px', flexShrink: 0 }} />
          <span style={{ fontSize: '0.7rem', color: '#495057', fontWeight: 500 }}>Medium ({severityCounts.MEDIUM || 0})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
          <div style={{ width: '10px', height: '10px', background: '#B71C1C', borderRadius: '2px', flexShrink: 0 }} />
          <span style={{ fontSize: '0.7rem', color: '#495057', fontWeight: 500 }}>High ({severityCounts.HIGH || 0})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
          <div style={{ width: '10px', height: '10px', background: '#808080', borderRadius: '2px', flexShrink: 0 }} />
          <span style={{ fontSize: '0.7rem', color: '#495057', fontWeight: 500 }}>No forecast data ({severityCounts.NO_DATA || 0})</span>
        </div>
      </div>
    </div>
  );
};

export default ForecastDateSelector;